/**
 * Punch items, Observations and Autodesk Issues — the records table
 * (Claude Design: `Led AI - Records Table.dc.html`).
 *
 * One page, three layouts over the same rows:
 *
 *  - **Table** — every property in its own column, a sticky header that
 *    sorts, a sticky footer that sums, sync state per row.
 *  - **List** — grouped (status by default; location, company or assignee),
 *    each group under a sticky band washed in its own colour.
 *  - **Board** — a column per status, a card per record.
 *
 * ## Where each piece of state lives
 *
 * Sort and the column filters are URL parameters (`?col=&dir=`, `?f.<key>=`)
 * written through the shell, like every other filter in the console, so a
 * narrowed table is a link. The layout and the list's grouping are per-viewer
 * conveniences and live in `localStorage`, as do the table's column widths
 * (drag a header's right edge; double-click it for the default). The view pill and the search box
 * are the page's own state: they are how a reader glances, not what they
 * share.
 *
 * ## What is NOT drawn from the artboard
 *
 *  - The view row's "+" (saved views) and the footer's "+ Add operation":
 *    neither has anything behind it, and a control that does nothing is
 *    worse than its absence.
 *  - The sidebar and the Ask dock — the console's own shell already draws
 *    both on every page.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { postItemEdit, postItemRetrySync, postMutation, type ApiError, type ConsoleRow, type ConsoleSite } from "../../api";
import { PhotoImage } from "../../components/PhotoImage";
import { readRecordPhoto } from "../../lib/recordPhoto";
import { DsIcon, type DsIconName } from "../../components/DsIcon";
import { ASK_COMPOSER_CLEARANCE, EmptyPanel, StaleBanner, usePagedList, PagedListEnd } from "../../components/chrome";
import {
  applyColumnFilters,
  columnFilterOptions,
  readColumnFilters,
  NO_VALUE,
  type ColumnFilterDef,
} from "../../components/columnFilter";
import { compareMaybe, compareText, readColumnSort, sortByColumn, type ColumnSort } from "../../components/columnSort";
import { PROCORE_MARK_URL } from "../../assets/procoreMark";
import { sharedSyncSource } from "../../lib/integrationName";
import type { ConsoleSeverity } from "../../../server/queries/listItems";
import { ADR, DS, FONT_MONO, FONT_TEXT, RT, withAlpha } from "../../tokens";
import { RECORDS_COMPACT_QUERY, RECORDS_FILTER_BAR, RECORDS_HEADER_ROW, RECORDS_ICON_BUTTON, RECORDS_ROW, RECORDS_TABLE_END, RECORDS_TOP_BAR } from "../../layout";
import { useMediaQuery } from "../../shell/useMediaQuery";
import { BoardCard } from "./BoardCard";
import { MoveRecordModal, type MoveFill } from "./MoveRecordModal";
import {
  BOARD_COLUMNS,
  canMove,
  moveRule,
  movePatch,
  type MoveRule,
  PRIORITY_LABEL,
  PRIORITY_RANK,
  avatarTone,
  companyInitials,
  companyTone,
  createdStamp,
  isClosed,
  recordFacts,
  shortDay,
  clock,
  statusesFor,
  type RecordFacts,
  type RecordVocab,
  type StatusDef,
} from "./recordModel";
import { CompanySwatch, MenuEyebrow, MenuRow, NoAvatar, PersonAvatar, Popover, PriorityBars, StatusGlyph } from "./parts";
import { NewRecordModal, type CreatedRecord } from "./NewRecordModal";

export type RecordsLayout = "list" | "board" | "table";
export type RecordsGroupBy = "status" | "location" | "company" | "assignee";
type ViewPill = "all" | "open" | "mine";

const KIND: Record<RecordVocab, { title: string; singular: string; plural: string; icon: DsIconName; scope: string }> = {
  punch: { title: "Punch items", singular: "punch item", plural: "punch items", icon: "flag", scope: "PUNCH" },
  obs: { title: "Observations", singular: "observation", plural: "observations", icon: "search", scope: "OBSERVATIONS" },
  issues: { title: "Issues", singular: "issue", plural: "issues", icon: "flag", scope: "ISSUES" },
};

/** Every URL key the records filters write — the shell's Clear all needs them. */
export const RECORD_FILTER_KEYS = ["status", "priority", "assignee", "company", "location", "trade", "type"] as const;

function categoryKey(vocab: RecordVocab): "trade" | "type" {
  return vocab === "punch" ? "trade" : "type";
}

function filterDefs(vocab: RecordVocab, factsOf: (row: ConsoleRow) => RecordFacts): Array<ColumnFilterDef & { label: string; icon: DsIconName }> {
  return [
    { key: "status", label: "Status", icon: "check", allLabel: "Any status", noneLabel: "No status", byCount: true, valueOf: (row) => factsOf(row).status.label },
    { key: "priority", label: "Priority", icon: "bolt", allLabel: "Any priority", noneLabel: "Unrated", byCount: true, valueOf: (row) => { const p = factsOf(row).priority; return p ? PRIORITY_LABEL[p] : null; } },
    { key: "assignee", label: "Assignee", icon: "person", allLabel: "Anyone", noneLabel: "Unassigned", byCount: true, valueOf: (row) => factsOf(row).assignee ?? null },
    { key: "company", label: "Company", icon: "device", allLabel: "All companies", noneLabel: "No company", byCount: true, valueOf: (row) => factsOf(row).company ?? null },
    { key: "location", label: "Location", icon: "pin", allLabel: "All locations", noneLabel: "No location", byCount: true, valueOf: (row) => factsOf(row).location ?? null },
    { key: categoryKey(vocab), label: vocab === "punch" ? "Trade" : "Type", icon: "list", allLabel: "Any", noneLabel: vocab === "punch" ? "No trade" : "No type", byCount: true, valueOf: (row) => factsOf(row).category ?? null },
  ];
}

/** Per-viewer conveniences — wrapped, because storage can throw. */
function readPref<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const value = globalThis.localStorage?.getItem(key);
    return value && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
  } catch {
    return fallback;
  }
}
function writePref(key: string, value: string) {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    /* a private window keeps the choice for this page only */
  }
}

/**
 * Column widths a viewer dragged, by column key (LED-1338). Only the columns
 * they touched are stored; every other column keeps its default width.
 */
export const COLUMN_WIDTHS_KEY = "ledai.records.columnWidths";
export const COLUMN_MIN_WIDTH = 60;
export const COLUMN_MAX_WIDTH = 900;
export function clampColumnWidth(width: number): number {
  return Math.round(Math.min(COLUMN_MAX_WIDTH, Math.max(COLUMN_MIN_WIDTH, width)));
}
function readColumnWidths(): Record<string, number> {
  try {
    const parsed: unknown = JSON.parse(globalThis.localStorage?.getItem(COLUMN_WIDTHS_KEY) ?? "{}");
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const widths: Record<string, number> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === "number" && Number.isFinite(value)) widths[key] = clampColumnWidth(value);
    }
    return widths;
  } catch {
    return {};
  }
}

const SYNC_ORDER: Record<string, number> = { synced: 0, pending: 1, failed: 2 };

function compareFacts(statuses: StatusDef[], siteName: (id: string) => string): Record<string, (a: RecordFacts, b: RecordFacts) => number> {
  const text = (pick: (f: RecordFacts) => string | undefined) => (a: RecordFacts, b: RecordFacts) =>
    compareMaybe(pick(a), pick(b), "asc", compareText);
  return {
    number: text((f) => f.number),
    title: (a, b) => compareText(a.row.title, b.row.title),
    project: (a, b) => compareText(siteName(a.row.jobSiteId), siteName(b.row.jobSiteId)),
    status: (a, b) => statuses.findIndex((s) => s.key === a.status.key) - statuses.findIndex((s) => s.key === b.status.key),
    priority: (a, b) => (a.priority ? PRIORITY_RANK[a.priority] : 0) - (b.priority ? PRIORITY_RANK[b.priority] : 0),
    assignee: text((f) => f.assignee),
    company: text((f) => f.company),
    location: text((f) => f.location),
    trade: text((f) => f.category),
    type: text((f) => f.category),
    due: (a, b) => compareMaybe(a.due?.getTime(), b.due?.getTime(), "asc", (l, r) => l - r),
    created: (a, b) => a.created.getTime() - b.created.getTime(),
    sync: (a, b) => (a.sync ? SYNC_ORDER[a.sync]! : 9) - (b.sync ? SYNC_ORDER[b.sync]! : 9),
  };
}

const NEWEST_FIRST: ColumnSort = { column: "created", direction: "desc" };

export interface RecordsWorkspaceProps {
  vocab: RecordVocab;
  /** The rows this view and project scope admit, before any filter here. */
  rows: ConsoleRow[];
  sites: ConsoleSite[];
  activeSiteId: string | null;
  search: string;
  me: { userId: string; name?: string };
  /** A `?severity=` deep link still narrows the server's rows; it is shown
   *  as a chip so the reader can see it and undo it. */
  severities: ConsoleSeverity[] | null;
  onSeverity: (next: ConsoleSeverity | null) => void;
  onColumnSort: (next: ColumnSort | null) => void;
  onColumnFilter: (column: string, value: string | null) => void;
  onClearFilters: () => void;
  hrefForItem: (itemId: string) => string;
  onOpenItem: (itemId: string) => void;
  refreshFailure: ApiError | null;
  emptyBody: string;
}

export function RecordsWorkspace(props: RecordsWorkspaceProps) {
  const { vocab, rows, sites, activeSiteId, search, me } = props;
  const kind = KIND[vocab];
  const statuses = statusesFor(vocab);
  const showSite = !activeSiteId;
  const showSync = vocab !== "issues";
  const canCreate = vocab !== "issues";
  const compact = useMediaQuery(RECORDS_COMPACT_QUERY);

  const [layout, setLayoutState] = useState<RecordsLayout>(() => readPref("ledai.records.layout", ["list", "board", "table"] as const, "table"));
  const [groupBy, setGroupByState] = useState<RecordsGroupBy>(() => readPref("ledai.records.groupBy", ["status", "location", "company", "assignee"] as const, "status"));
  const setLayout = (next: RecordsLayout) => { setLayoutState(next); writePref("ledai.records.layout", next); setHover(null); };
  const setGroupBy = (next: RecordsGroupBy) => { setGroupByState(next); writePref("ledai.records.groupBy", next); };
  const [view, setView] = useState<ViewPill>("all");
  const [q, setQ] = useState("");
  const [menu, setMenu] = useState<null | "filter" | "group" | "export">(null);
  const [filterPage, setFilterPage] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const [tip, setTip] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [toast, setToast] = useState<{ text: string; tone: "ok" | "warn" } | null>(null);
  const [retrying, setRetrying] = useState<Record<string, boolean>>({});
  // Board moves: the column a card was dropped in, held until the rows say so.
  const [moved, setMoved] = useState<Record<string, StatusDef>>({});
  const [dragging, setDragging] = useState<string | null>(null);
  const [dropOn, setDropOn] = useState<string | null>(null);
  const [pendingMove, setPendingMove] = useState<{ f: RecordFacts; to: StatusDef; rule: Exclude<MoveRule, "none"> } | null>(null);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(readColumnWidths);
  const [resizing, setResizing] = useState<string | null>(null);
  // A drag that ends over the header would otherwise land as a click and sort.
  const justResized = useRef(false);
  const stopResize = useRef<(() => void) | undefined>(undefined);
  useEffect(() => () => stopResize.current?.(), []);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const tipTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => { clearTimeout(hoverTimer.current); clearTimeout(tipTimer.current); clearTimeout(toastTimer.current); }, []);
  // A different population starts on its own terms.
  useEffect(() => { setView("all"); setQ(""); setMenu(null); }, [vocab, activeSiteId]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") setMenu(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const flash = (text: string, tone: "ok" | "warn" = "ok") => {
    setToast({ text, tone });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), tone === "ok" ? 2600 : 6000);
  };

  // ---- the rows, read once ------------------------------------------------
  const today = useMemo(() => new Date(), [rows]);
  const factsById = useMemo(() => new Map(rows.map((row) => [row.id, recordFacts(row, vocab, today)])), [rows, vocab, today]);
  const factsOf = (row: ConsoleRow) => {
    const facts = factsById.get(row.id) ?? recordFacts(row, vocab, today);
    const to = moved[row.id];
    return to ? { ...facts, status: to } : facts;
  };
  // A move is the rows' to report once they have caught up with it.
  useEffect(() => {
    setMoved((current) => {
      const keep = Object.fromEntries(Object.entries(current).filter(([id, to]) => factsById.get(id)?.status.key !== to.key));
      return Object.keys(keep).length === Object.keys(current).length ? current : keep;
    });
  }, [factsById]);
  const defs = filterDefs(vocab, factsOf);
  const active = readColumnFilters(search, defs);
  const siteName = (id: string) => sites.find((site) => site.id === id)?.name || id;
  const mine = (f: RecordFacts) =>
    f.row.workspaceAssignee?.userId === me.userId || (!!me.name && f.assignee?.toLowerCase() === me.name.toLowerCase());

  const viewRows = rows.filter((row) => {
    if (view === "open") return !isClosed(factsOf(row).status);
    if (view === "mine") return mine(factsOf(row));
    return true;
  });
  const needle = q.trim().toLowerCase();
  const columnSort = readColumnSort(search) ?? NEWEST_FIRST;
  const shownFacts = sortByColumn(
    applyColumnFilters(viewRows, defs, active)
      .map(factsOf)
      .filter((f) => !needle || [f.row.title, f.assignee, f.company, f.location, f.number].some((v) => v?.toLowerCase().includes(needle))),
    columnSort,
    compareFacts(statuses, siteName),
  );
  const openCount = rows.filter((row) => !isClosed(factsOf(row).status)).length;
  const optionsFor = (def: ColumnFilterDef) => columnFilterOptions(viewRows, defs, active, def, () => 0);
  const activeChips = defs.filter((def) => active[def.key]).map((def) => ({
    def,
    value: active[def.key] === NO_VALUE ? def.noneLabel : active[def.key]!,
  }));
  const anyFilter = activeChips.length > 0 || (props.severities?.length ?? 0) > 0;

  const paged = usePagedList(`${vocab}|${activeSiteId ?? ""}|${layout}|${view}|${needle}|${search}`, shownFacts.length);
  // The board draws every card: each column scrolls on its own, so there is
  // no single page end for a "load more" sentinel to sit at.
  const drawnFacts = layout === "board" ? shownFacts : shownFacts.slice(0, paged.shown);

  // ---- hover peeks ------------------------------------------------------
  // The row carries its first photos already signed; fetching them the moment
  // the pointer arrives means they are decoded by the time the peek shows.
  const hoverIn = (f: RecordFacts) => {
    clearTimeout(hoverTimer.current);
    preloadPhotos(f.row);
    hoverTimer.current = setTimeout(() => setHover(f.row.id), 380);
  };
  const hoverOut = () => { clearTimeout(hoverTimer.current); setHover(null); };
  const tipIn = (id: string) => { clearTimeout(tipTimer.current); tipTimer.current = setTimeout(() => setTip(id), 220); };
  const tipOut = () => { clearTimeout(tipTimer.current); setTip(null); };

  const open = (f: RecordFacts, event: React.MouseEvent | React.KeyboardEvent) => {
    if ("metaKey" in event && (event.metaKey || event.ctrlKey)) {
      window.open(props.hrefForItem(f.row.id), "_blank", "noopener");
      return;
    }
    props.onOpenItem(f.row.id);
  };
  const rowLink = (f: RecordFacts) => ({
    role: "link" as const,
    tabIndex: 0,
    "data-item-id": f.row.id,
    onClick: (event: React.MouseEvent) => open(f, event),
    onKeyDown: (event: React.KeyboardEvent) => { if (event.key === "Enter") open(f, event); },
    onMouseEnter: () => hoverIn(f),
    onMouseLeave: hoverOut,
  });

  // ---- board moves -------------------------------------------------------
  const commitMove = async (f: RecordFacts, to: StatusDef, fill: MoveFill = {}): Promise<string | null> => {
    setMoved((current) => ({ ...current, [f.row.id]: to }));
    const result = await postItemEdit(f.row.kind, f.row.id, search, movePatch(vocab, to, fill, f.row));
    if (!result.ok) {
      setMoved(({ [f.row.id]: _gone, ...rest }) => rest);
      const message = result.error || `Could not move it to ${to.label}`;
      if (!pendingMove) flash(message, "warn");
      return message;
    }
    // A photo is its own write, after the move: the move has landed either way.
    let photoFailures = 0;
    for (const file of fill.photos ?? []) {
      try {
        const upload = await postMutation(`/api/items/${encodeURIComponent(f.row.kind)}/${encodeURIComponent(f.row.id)}/media`, search, await readRecordPhoto(file));
        if (!upload.ok) photoFailures += 1;
      } catch {
        photoFailures += 1;
      }
    }
    const num = f.number ? `#${f.number}` : vocab === "punch" ? "Punch item" : "Observation";
    if (photoFailures) flash(`${num} moved to ${to.label} · ${photoFailures === 1 ? "a photo" : `${photoFailures} photos`} did not upload`, "warn");
    else if (fill.assignee && to.key === "work_required") flash(`${num} sent to ${fill.assignee.name} · ${to.label}`);
    else if (to.key === "ready_for_review" && vocab === "punch") flash(`${num} → ${to.label} · manager notified`);
    else flash(`${num} → ${to.label}`);
    return null;
  };
  const dropOnColumn = (to: StatusDef) => {
    const f = drawnFacts.find((entry) => entry.row.id === dragging);
    setDragging(null);
    setDropOn(null);
    if (!f || f.status.key === to.key) return;
    const rule = moveRule(vocab, f.status.key, to.key);
    if (rule === "none") void commitMove(f, to);
    else setPendingMove({ f, to, rule });
  };
  const draggedFrom = dragging ? drawnFacts.find((entry) => entry.row.id === dragging)?.status.key : undefined;
  const dragProps = (f: RecordFacts) =>
    canMove(f.row, vocab)
      ? {
          draggable: true,
          "data-draggable": "true",
          onDragStart: (event: React.DragEvent) => {
            event.dataTransfer.effectAllowed = "move";
            event.dataTransfer.setData("text/plain", f.row.id);
            hoverOut();
            setDragging(f.row.id);
          },
          onDragEnd: () => { setDragging(null); setDropOn(null); },
        }
      : {};

  const retry = async (f: RecordFacts) => {
    setRetrying((current) => ({ ...current, [f.row.id]: true }));
    const result = await postItemRetrySync(f.row.kind, f.row.id, search);
    setRetrying(({ [f.row.id]: _done, ...rest }) => rest);
    if (!result.ok) flash(result.error || "Could not retry the sync", "warn");
    else flash("Sync retried");
  };

  const onCreated = (created: CreatedRecord) => {
    const noun = vocab === "punch" ? "Punch item" : "Observation";
    if (created.sync.error) {
      flash(`${noun} created · ${created.sync.error}`, "warn");
    } else if (created.sync.sentToAssignee) {
      flash(`${noun} created · sent to ${created.assignee ?? "the assignee"}${created.sync.workflowStatus ? ` · ${humanizeWorkflow(created.sync.workflowStatus)}` : ""}`);
    } else if (created.sync.provider === "procore") {
      flash(`${noun} created · synced to Procore as ${humanizeWorkflow(created.sync.workflowStatus ?? "initiated")}`);
    } else {
      flash(`${noun} created`);
    }
  };

  // ---- columns ----------------------------------------------------------
  const defaultColumns: Array<{ key: string; label: string; icon: DsIconName; width: number }> = [
    { key: "title", label: "Title", icon: kind.icon, width: 300 },
    ...(showSite ? [{ key: "project", label: "Project", icon: "pin" as const, width: 170 }] : []),
    { key: "status", label: "Status", icon: "check", width: 195 },
    { key: "priority", label: "Priority", icon: "bolt", width: 110 },
    { key: "assignee", label: "Assignee", icon: "person", width: 160 },
    { key: "company", label: "Company", icon: "device", width: 170 },
    { key: "location", label: "Location", icon: "pin", width: 230 },
    { key: categoryKey(vocab), label: vocab === "punch" ? "Trade" : "Type", icon: "list", width: 150 },
    { key: "due", label: "Due date", icon: "calendar", width: 130 },
    { key: "created", label: "Created", icon: "calendar", width: 170 },
    // "Procore" only when every row is on a Procore project — an Autodesk
    // customer must never read Procore copy (LED-763).
    ...(showSync ? [{ key: "sync", label: sharedSyncSource(rows) === "procore" ? "Procore" : "Sync", icon: "download" as const, width: 180 }] : []),
  ];
  const columns = defaultColumns.map((column) => ({ ...column, defaultWidth: column.width, width: columnWidths[column.key] ?? column.width }));
  const cols = columns.map((column) => `${column.width}px`).join(" ");
  const tableMinW = columns.reduce((sum, column) => sum + column.width, 0);

  const saveColumnWidth = (key: string, width: number | null) => {
    const stored = readColumnWidths();
    if (width === null) delete stored[key];
    else stored[key] = width;
    writePref(COLUMN_WIDTHS_KEY, JSON.stringify(stored));
  };
  const startResize = (event: React.PointerEvent, key: string, from: number) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    stopResize.current?.();
    const startX = event.clientX;
    let width = from;
    const body = document.body.style;
    const before = { cursor: body.cursor, userSelect: body.userSelect };
    body.cursor = "col-resize";
    body.userSelect = "none";
    setResizing(key);
    const move = (moveEvent: PointerEvent) => {
      width = clampColumnWidth(from + moveEvent.clientX - startX);
      setColumnWidths((current) => (current[key] === width ? current : { ...current, [key]: width }));
    };
    const end = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      body.cursor = before.cursor;
      body.userSelect = before.userSelect;
      stopResize.current = undefined;
    };
    const up = () => {
      end();
      setResizing(null);
      if (width !== from) {
        saveColumnWidth(key, width);
        justResized.current = true;
        setTimeout(() => { justResized.current = false; }, 0);
      }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    stopResize.current = end;
  };
  const resetColumnWidth = (key: string) => {
    setColumnWidths(({ [key]: _gone, ...rest }) => rest);
    saveColumnWidth(key, null);
  };

  const sortBy = (key: string) => {
    if (justResized.current) return;
    const direction = columnSort.column === key ? (columnSort.direction === "asc" ? "desc" : "asc") : "asc";
    props.onColumnSort({ column: key, direction });
  };

  const exportCsv = () => {
    const header = ["Number", "Title", ...(showSite ? ["Project"] : []), "Status", "Priority", "Assignee", "Company", "Location", vocab === "punch" ? "Trade" : "Type", "Due date", "Created", ...(showSync ? [sharedSyncSource(rows) === "procore" ? "Procore" : "Sync"] : [])];
    const lines = shownFacts.map((f) => [
      f.number ?? "", f.row.title, ...(showSite ? [siteName(f.row.jobSiteId)] : []), f.status.label, f.priority ? PRIORITY_LABEL[f.priority] : "",
      f.assignee ?? "", f.company ?? "", f.location ?? "", f.category ?? "", f.due ? `${shortDay(f.due)}, ${f.due.getFullYear()}` : "", createdStamp(f.created),
      ...(showSync ? [f.sync ? syncLabel(f.sync) : ""] : []),
    ]);
    const csv = [header, ...lines].map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${kind.plural.replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setMenu(null);
  };

  // ---- groups (list + board) --------------------------------------------
  const statusGroups = statuses
    .map((status) => ({ status, facts: drawnFacts.filter((f) => f.status.key === status.key) }))
    .concat(
      // Anything the vocabulary did not predict, named after itself.
      [...new Map(drawnFacts.filter((f) => !statuses.some((s) => s.key === f.status.key)).map((f) => [f.status.key, f.status])).values()]
        .map((status) => ({ status, facts: drawnFacts.filter((f) => f.status.key === status.key) })),
    );

  return (
    <div
      data-testid="records-workspace"
      data-vocab={vocab}
      data-layout={layout}
      onClick={() => setMenu(null)}
      style={{ flex: 1, minHeight: 0, minWidth: 0, display: "flex", flexDirection: "column", background: RT.pane, fontFamily: FONT_TEXT, color: DS.ink, position: "relative", WebkitFontSmoothing: "antialiased" }}
    >
      {/* ---------- top bar: title + views ---------- */}
      <div data-testid="records-topbar" style={{ flex: "none", display: "flex", alignItems: "center", gap: 10, ...RECORDS_TOP_BAR, borderBottom: `1px solid ${DS.line}` }}>
        <h1 data-testid="records-title" style={{ margin: 0, flex: "none", display: "inline-flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 700, letterSpacing: "-0.2px", whiteSpace: "nowrap" }}>
          <DsIcon name={kind.icon} size={16} color={DS.ink} />
          {kind.title}
        </h1>
        <span style={{ flex: "none", width: 1, height: 18, background: "rgba(10,10,10,0.1)", margin: "0 4px" }} />
        {/* Narrow, the pills scroll; the fade says there is more to the right. */}
        <div className="rt-noscroll" role="tablist" aria-label="Views" style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 10, overflowX: "auto", ...(compact ? { maskImage: RT.viewsFade, WebkitMaskImage: RT.viewsFade } : {}) }}>
          {([["all", "All", rows.length], ["open", "Open", openCount], ["mine", "Assigned to me", null]] as const).map(([id, label, count]) => {
            const on = view === id;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={on}
                data-testid={`records-view-${id}`}
                onClick={() => setView(id)}
                style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 30, padding: "0 10px", borderRadius: 8, background: on ? RT.pillOn : "transparent", border: `1px solid ${on ? "rgba(10,10,10,0.08)" : "transparent"}`, font: "inherit", fontSize: 13, fontWeight: 600, color: on ? DS.ink : DS.body2, cursor: "pointer", whiteSpace: "nowrap" }}
              >
                {label}
                {count !== null && <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: RT.count }}>{count}</span>}
              </button>
            );
          })}
        </div>
        <span style={{ position: "relative", flex: "none" }}>
          <button
            type="button"
            className="rt-hover"
            data-testid="records-export"
            aria-expanded={menu === "export"}
            aria-label={compact ? "Export" : undefined}
            title={compact ? "Export" : undefined}
            onClick={(event) => { event.stopPropagation(); setMenu(menu === "export" ? null : "export"); }}
            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, height: 32, ...(compact ? { width: RECORDS_ICON_BUTTON, padding: 0 } : { padding: "0 12px" }), borderRadius: 8, border: "none", background: "transparent", font: "inherit", fontSize: 13, fontWeight: 600, color: RT.cell, cursor: "pointer", whiteSpace: "nowrap" }}
          >
            <DsIcon name="download" size={14} color={RT.cell} />
            {!compact && "Export"}
            {!compact && <DsIcon name="chevron" size={11} color={DS.meta} style={{ transform: "rotate(90deg)" }} />}
          </button>
          {menu === "export" && (
            <Popover width={220} right={0} left={undefined} z={50}>
              <MenuEyebrow>Export</MenuEyebrow>
              <MenuRow testId="records-export-csv" icon={<DsIcon name="download" size={14} color={DS.body2} />} onPick={exportCsv} trailing={<span style={{ fontFamily: FONT_MONO, fontSize: 10.5, color: RT.count }}>{shownFacts.length}</span>}>
                Download CSV
              </MenuRow>
            </Popover>
          )}
        </span>
        {canCreate && (
          <button
            type="button"
            className="rt-hover"
            data-testid="records-new"
            aria-label={compact ? `New ${kind.singular}` : undefined}
            title={compact ? `New ${kind.singular}` : undefined}
            onClick={(event) => { event.stopPropagation(); setMenu(null); setNewOpen(true); }}
            style={{ flex: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, height: 32, ...(compact ? { width: RECORDS_ICON_BUTTON, padding: 0 } : { padding: "0 12px" }), borderRadius: 8, border: "1px solid rgba(10,10,10,0.12)", background: RT.pane, font: "inherit", fontSize: 13, fontWeight: 700, color: DS.ink, cursor: "pointer", whiteSpace: "nowrap" }}
          >
            <DsIcon name="plus" size={14} color={DS.ink} />
            {!compact && `New ${kind.singular}`}
          </button>
        )}
      </div>

      {/* ---------- filter bar ---------- */}
      <div data-testid="records-filterbar" style={{ flex: "none", display: "flex", alignItems: "center", gap: 8, ...RECORDS_FILTER_BAR, flexWrap: "wrap", borderBottom: `1px solid ${DS.line}` }}>
        <span style={{ position: "relative" }}>
          <button
            type="button"
            data-testid="records-filter"
            aria-expanded={menu === "filter"}
            onClick={(event) => { event.stopPropagation(); setFilterPage(null); setMenu(menu === "filter" ? null : "filter"); }}
            style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 30, padding: "0 11px", borderRadius: 8, border: "1px solid rgba(10,10,10,0.12)", background: menu === "filter" ? RT.pillOn : RT.pane, font: "inherit", fontSize: 13, fontWeight: 600, color: DS.ink, cursor: "pointer" }}
          >
            <DsIcon name="filter" size={13} color={DS.ink} />
            Filter
          </button>
          {menu === "filter" && (
            <Popover width={filterPage ? 260 : 220} testId="records-filter-menu">
              {filterPage === null ? (
                <>
                  <MenuEyebrow>Filter by</MenuEyebrow>
                  {defs.map((def) => (
                    <MenuRow
                      key={def.key}
                      testId={`records-filter-field-${def.key}`}
                      icon={<DsIcon name={def.icon} size={14} color={DS.body2} />}
                      trailing={<DsIcon name="chevron" size={11} color={DS.dashed} />}
                      onPick={() => setFilterPage(def.key)}
                    >
                      {def.label}
                    </MenuRow>
                  ))}
                </>
              ) : (
                (() => {
                  const def = defs.find((entry) => entry.key === filterPage)!;
                  const options = optionsFor(def).filter((option) => option.value !== null);
                  const pickValue = (value: string) => { props.onColumnFilter(def.key, value); setMenu(null); setFilterPage(null); };
                  return (
                    <>
                      <button
                        type="button"
                        className="rt-hover"
                        onClick={(event) => { event.stopPropagation(); setFilterPage(null); }}
                        style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, height: 30, padding: "0 8px", marginBottom: 2, borderRadius: 8, border: "none", background: "transparent", font: "inherit", fontSize: 12.5, color: DS.body2, cursor: "pointer" }}
                      >
                        <DsIcon name="chevron" size={11} color={DS.meta} style={{ transform: "rotate(180deg)" }} />
                        {def.label}
                      </button>
                      <div className="rt-noscroll" style={{ maxHeight: 300, overflowY: "auto" }}>
                        {def.key === "status"
                          ? statuses.map((status) => {
                              const count = viewRows.filter((row) => factsOf(row).status.key === status.key).length;
                              return (
                                <MenuRow key={status.key} testId="records-filter-option" selected={active.status === status.label} icon={<StatusGlyph status={status} />} onPick={() => pickValue(status.label)} trailing={<span style={{ fontFamily: FONT_MONO, fontSize: 10.5, color: RT.count }}>{count}</span>}>
                                  {status.label}
                                </MenuRow>
                              );
                            })
                          : options.map((option) => (
                              <MenuRow
                                key={option.value}
                                testId="records-filter-option"
                                selected={option.selected}
                                icon={filterOptionIcon(def.key, option.value === NO_VALUE ? null : option.label)}
                                onPick={() => pickValue(option.value!)}
                                trailing={<span style={{ fontFamily: FONT_MONO, fontSize: 10.5, color: RT.count }}>{option.count}</span>}
                              >
                                {option.label}
                              </MenuRow>
                            ))}
                        {def.key !== "status" && options.length === 0 && (
                          <span style={{ display: "block", padding: 10, fontSize: 12.5, color: RT.count }}>Nothing to filter by</span>
                        )}
                      </div>
                    </>
                  );
                })()
              )}
            </Popover>
          )}
        </span>

        {(props.severities ?? []).length > 0 && (
          <FilterChip icon="bolt" field="Priority" value={(props.severities ?? []).map((s) => PRIORITY_LABEL[s as keyof typeof PRIORITY_LABEL] ?? s).join(", ")} onRemove={() => props.onSeverity(null)} />
        )}
        {activeChips.map(({ def, value }) => (
          <FilterChip key={def.key} icon={def.icon} field={def.label} value={value} onRemove={() => props.onColumnFilter(def.key, null)} />
        ))}
        {anyFilter && (
          <button
            type="button"
            data-testid="clear-filters"
            className="rt-hover-ink"
            onClick={props.onClearFilters}
            style={{ height: 30, padding: "0 8px", borderRadius: 8, border: "none", background: "transparent", font: "inherit", fontSize: 12.5, fontWeight: 600, color: DS.meta, cursor: "pointer" }}
          >
            Clear
          </button>
        )}

        <span style={{ flex: "0 1 220px", minWidth: 120, display: "inline-flex", alignItems: "center", gap: 8, height: 30, padding: "0 10px", borderRadius: 8, border: "1px solid rgba(10,10,10,0.12)", background: RT.pane }}>
          <DsIcon name="search" size={13} color={DS.meta} />
          <input
            data-testid="records-search"
            className="rt-input"
            value={q}
            onInput={(event) => setQ(event.currentTarget.value)}
            placeholder={`Search ${kind.plural}`}
            aria-label={`Search ${kind.plural}`}
            style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", font: "inherit", fontSize: 13, color: DS.ink }}
          />
        </span>
        <span style={{ flex: 1 }} />
        <span data-testid="records-count" style={{ flex: "none", fontFamily: FONT_MONO, fontSize: 10, letterSpacing: 1, color: RT.count, whiteSpace: "nowrap" }}>
          {shownFacts.length} OF {rows.length}
        </span>

        {layout === "list" && (
          <span style={{ position: "relative", flex: "none" }}>
            <button
              type="button"
              className="rt-hover"
              data-testid="records-group"
              aria-expanded={menu === "group"}
              onClick={(event) => { event.stopPropagation(); setMenu(menu === "group" ? null : "group"); }}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 30, padding: "0 11px", borderRadius: 8, border: "1px solid rgba(10,10,10,0.12)", background: groupBy !== "status" ? RT.pillOn : RT.pane, font: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
            >
              <DsIcon name="list" size={13} color={DS.ink} />
              <span style={{ color: DS.body2 }}>Group</span>
              <span style={{ color: DS.ink }}>{GROUP_LABEL[groupBy]}</span>
              <DsIcon name="chevron" size={10} color={DS.meta} style={{ transform: "rotate(90deg)" }} />
            </button>
            {menu === "group" && (
              <Popover width={200} z={50}>
                <MenuEyebrow>Group by</MenuEyebrow>
                {(["status", "location", "company", "assignee"] as const).map((id) => (
                  <MenuRow
                    key={id}
                    testId={`records-group-${id}`}
                    selected={groupBy === id}
                    icon={<DsIcon name={GROUP_ICON[id]} size={13} color={DS.body2} />}
                    trailing={groupBy === id ? <DsIcon name="check" size={12} color={DS.ink} /> : undefined}
                    onPick={() => { setGroupBy(id); setMenu(null); }}
                  >
                    {GROUP_LABEL[id]}
                  </MenuRow>
                ))}
              </Popover>
            )}
          </span>
        )}

        <span role="radiogroup" aria-label="Layout" style={{ flex: "none", display: "inline-flex", alignItems: "center", height: 30, padding: 2, borderRadius: 8, border: "1px solid rgba(10,10,10,0.12)", background: RT.pane, gap: 2, boxSizing: "border-box" }}>
          {([["list", "List", "list"], ["board", "Board", "device"], ["table", "Table", "filter"]] as const).map(([id, label, icon]) => {
            const on = layout === id;
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={on}
                title={label}
                data-testid={`records-layout-${id}`}
                onClick={() => setLayout(id)}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 24, padding: "0 9px", borderRadius: 6, border: "none", background: on ? DS.ink : "transparent", font: "inherit", fontSize: 12.5, fontWeight: 600, color: on ? RT.pane : DS.body2, cursor: "pointer", whiteSpace: "nowrap" }}
              >
                <DsIcon name={icon} size={12} color="currentColor" />
                {label}
              </button>
            );
          })}
        </span>
      </div>

      {props.refreshFailure && (
        <div style={{ flex: "none", padding: "10px 16px 0" }}>
          <StaleBanner failure={props.refreshFailure} />
        </div>
      )}

      {/* ---------- body ---------- */}
      {shownFacts.length === 0 ? (
        <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: `28px 16px ${ASK_COMPOSER_CLEARANCE}px`, paddingBottom: `var(--ask-composer-clearance, ${ASK_COMPOSER_CLEARANCE}px)` }}>
          {rows.length === 0 ? (
            <EmptyPanel title={`No ${kind.plural} yet`} body={props.emptyBody} />
          ) : (
            <EmptyPanel title="Nothing matches" body={anyFilter ? "Clear in the filter row widens it again." : "Try another view or search."} />
          )}
        </div>
      ) : layout === "table" ? (
        <div
          className="rt-noscroll"
          data-testid="records-table"
          // The table's viewport ends where the Ask composer's rail begins, so
          // the sticky totals row rests on top of it instead of under it.
          style={{ flex: 1, minHeight: 0, overflow: "auto", marginBottom: RECORDS_TABLE_END, borderBottom: "1px solid rgba(10,10,10,0.08)" }}
        >
          <div style={{ minWidth: tableMinW, width: tableMinW }}>
            <div role="row" style={{ position: "sticky", top: 0, zIndex: 5, display: "grid", gridTemplateColumns: cols, background: RT.strip, borderBottom: "1px solid rgba(10,10,10,0.08)" }}>
              {columns.map((column, index) => {
                const sorted = columnSort.column === column.key;
                return (
                  <button
                    key={column.key}
                    type="button"
                    role="columnheader"
                    className="rt-th"
                    data-testid="records-th"
                    data-column={column.key}
                    aria-sort={sorted ? (columnSort.direction === "asc" ? "ascending" : "descending") : "none"}
                    title={column.label}
                    onClick={() => sortBy(column.key)}
                    style={{ ...TH, position: "relative", borderRight: index === columns.length - 1 ? "none" : TH.borderRight }}
                  >
                    <DsIcon name={column.icon} size={13} color={DS.meta} />
                    <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{column.label}</span>
                    {sorted && <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: DS.ink, marginLeft: "auto" }}>{columnSort.direction === "asc" ? "↑" : "↓"}</span>}
                    {/* Drag to resize; double-click for the default width (LED-1338). */}
                    <span
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Resize ${column.label} column`}
                      aria-valuenow={column.width}
                      aria-valuemin={COLUMN_MIN_WIDTH}
                      aria-valuemax={COLUMN_MAX_WIDTH}
                      className="rt-resize"
                      data-testid="records-col-resize"
                      data-column={column.key}
                      data-active={resizing === column.key || undefined}
                      title="Drag to resize · double-click to reset"
                      onPointerDown={(event) => startResize(event, column.key, column.width)}
                      onClick={(event) => event.stopPropagation()}
                      onDoubleClick={(event) => { event.stopPropagation(); resetColumnWidth(column.key); }}
                      style={{ position: "absolute", top: 0, bottom: 0, right: index === columns.length - 1 ? 0 : -5, width: 9, zIndex: 1, cursor: "col-resize", touchAction: "none" }}
                    />
                  </button>
                );
              })}
            </div>

            {drawnFacts.map((f) => (
              <div key={f.row.id} className="rt-row" data-testid="records-row" {...rowLink(f)} style={{ display: "grid", gridTemplateColumns: cols, borderBottom: "1px solid rgba(10,10,10,0.06)", cursor: "pointer", position: "relative", outline: "none" }}>
                <div className="rt-cell" style={{ ...CELL, gap: 10, position: "relative", overflow: "visible" }}>
                  <span data-testid="records-number" style={{ flex: "none", width: 34, fontFamily: FONT_MONO, fontSize: 10, color: DS.dashed, overflow: "hidden", textOverflow: "ellipsis" }}>{f.number ? `#${f.number}` : ""}</span>
                  <a
                    href={props.hrefForItem(f.row.id)}
                    className="rt-link"
                    data-testid="row-title"
                    tabIndex={-1}
                    onClick={(event) => event.preventDefault()}
                    title={f.row.title}
                    style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", fontWeight: 600, letterSpacing: "-0.1px", color: DS.ink }}
                  >
                    {f.row.title}
                  </a>
                  {f.photos > 0 && <PhotoCount n={f.photos} />}
                  {hover === f.row.id && f.photos > 0 && <PhotoPeek row={f.row} count={f.photos} style={{ left: 48, top: 42 }} />}
                </div>
                {showSite && (
                  <div className="rt-cell" style={{ ...CELL, color: RT.cell }}>
                    <span style={ELLIPSIS} title={siteName(f.row.jobSiteId)}>{siteName(f.row.jobSiteId)}</span>
                  </div>
                )}
                <div className="rt-cell" onMouseEnter={() => tipIn(f.row.id)} onMouseLeave={tipOut} style={{ ...CELL, gap: 8, position: "relative", overflow: "visible", cursor: "default" }}>
                  <StatusGlyph status={f.status} />
                  <span data-testid="records-status" style={ELLIPSIS}>{f.status.label}</span>
                  {tip === f.row.id && <StatusTip who={f.tipWho} what={f.tipWhat} style={{ left: 14, top: 40 }} />}
                </div>
                <div className="rt-cell" style={{ ...CELL, gap: 8 }}>
                  <PriorityBars priority={f.priority} />
                  <span style={f.priority ? undefined : { color: DS.dashed }}>{f.priority ? PRIORITY_LABEL[f.priority] : "—"}</span>
                </div>
                <div className="rt-cell" style={{ ...CELL, gap: 8 }}>
                  {f.assignee ? (
                    <>
                      <PersonAvatar name={f.assignee} />
                      <span style={ELLIPSIS}>{f.assignee}</span>
                    </>
                  ) : (
                    <span style={{ color: DS.dashed }}>—</span>
                  )}
                </div>
                <div className="rt-cell" style={{ ...CELL, gap: 8 }}>
                  {f.company ? <CompanyBadge name={f.company} /> : <span style={{ color: DS.dashed }}>—</span>}
                </div>
                <div className="rt-cell" style={{ ...CELL, gap: 6, color: RT.cell }}>
                  {f.location ? (
                    <>
                      <DsIcon name="pin" size={12} color={DS.dashed} />
                      <span style={ELLIPSIS} title={f.location}>{f.location}</span>
                    </>
                  ) : (
                    <span style={{ color: DS.dashed }}>—</span>
                  )}
                </div>
                <div className="rt-cell" style={{ ...CELL, color: f.category ? RT.cell : DS.dashed }}>
                  <span style={ELLIPSIS}>{f.category ?? "—"}</span>
                </div>
                <div className="rt-cell" data-testid="records-due" data-overdue={f.overdue || undefined} style={{ ...CELL, color: f.overdue ? RT.overdue : f.due ? RT.cell : DS.dashed }}>
                  {f.due && !isClosed(f.status) ? `${shortDay(f.due)}, ${f.due.getFullYear()}` : "—"}
                </div>
                <div className="rt-cell" data-testid="created-date" style={{ ...CELL, color: DS.body2 }}>{createdStamp(f.created)}</div>
                {showSync && (
                  <div className="rt-cell" style={{ ...CELL, gap: 6, borderRight: "none" }}>
                    <SyncCell f={f} busy={!!retrying[f.row.id]} onRetry={() => void retry(f)} />
                  </div>
                )}
              </div>
            ))}

            <PagedListEnd shown={paged.shown} total={shownFacts.length} noun={kind.singular} nouns={kind.plural} sentinel={paged.sentinel} />

            <div data-testid="records-footer" style={{ position: "sticky", bottom: 0, marginTop: -1, display: "grid", gridTemplateColumns: cols, background: RT.strip, borderTop: "1px solid rgba(10,10,10,0.08)", zIndex: 4 }}>
              {columns.map((column, index) => (
                <div key={column.key} style={{ ...CELL, height: 36, fontSize: 12.5, color: DS.meta, borderRight: index === columns.length - 1 ? "none" : CELL.borderRight }}>
                  {footerFor(column.key, shownFacts)}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : layout === "list" ? (
        <div className="rt-noscroll" data-testid="records-list" style={{ flex: 1, minHeight: 0, overflow: "auto", padding: `6px 0 ${ASK_COMPOSER_CLEARANCE}px`, paddingBottom: `var(--ask-composer-clearance, ${ASK_COMPOSER_CLEARANCE}px)`, background: RT.pane }}>
          {(groupBy === "status"
            ? statusGroups
                .filter((group) => group.facts.length > 0 || (vocab === "punch" && group.status.flow))
                .map((group) => ({ key: group.status.key, label: group.status.label, facts: group.facts, color: group.status.color, mark: <StatusGlyph status={group.status} /> }))
            : listGroups(drawnFacts, groupBy)
          ).map((group) => (
            <section key={group.key} data-testid="records-group-section" data-group={group.label}>
              <div
                data-testid="records-band"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  height: 40,
                  margin: "6px 12px 2px",
                  padding: "0 14px",
                  // The band: the group's own colour, washed in from the left
                  // and gone by three quarters of the width, over warm paper.
                  background: `linear-gradient(90deg, ${withAlpha(group.color, 0.035)} 0%, ${withAlpha(group.color, 0.018)} 35%, ${withAlpha(group.color, 0)} 75%), ${RT.band}`,
                  borderRadius: 8,
                  position: "sticky",
                  top: 0,
                  zIndex: 4,
                  // Covers the rows scrolling up behind the band's top margin.
                  boxShadow: `0 -6px 0 0 ${RT.pane}`,
                }}
              >
                {group.mark}
                <span style={{ fontSize: 13.5, fontWeight: 700 }}>{group.label}</span>
                <span style={{ fontFamily: FONT_MONO, fontSize: 10.5, color: RT.count }}>{group.facts.length}</span>
                <span style={{ flex: 1 }} />
                {canCreate && (
                  <button
                    type="button"
                    className="rt-hover"
                    aria-label={`New ${kind.singular}`}
                    onClick={(event) => { event.stopPropagation(); setNewOpen(true); }}
                    style={{ display: "inline-flex", width: 24, height: 24, alignItems: "center", justifyContent: "center", border: "none", borderRadius: 6, background: "transparent", cursor: "pointer" }}
                  >
                    <DsIcon name="plus" size={13} color={RT.count} />
                  </button>
                )}
              </div>
              {group.facts.map((f) => (
                <div key={f.row.id} className="rt-lrow" data-testid="records-row" {...rowLink(f)} style={{ position: "relative", display: "flex", alignItems: "center", gap: 12, height: 54, margin: "0 12px", padding: "0 14px", borderRadius: 8, cursor: "pointer", outline: "none" }}>
                  <span style={{ width: 12, display: "inline-flex" }}><PriorityBars priority={f.priority} /></span>
                  <span style={{ flex: "none", width: 52, fontFamily: FONT_MONO, fontSize: 11, color: DS.meta }}>{f.number ? `#${f.number}` : ""}</span>
                  <span onMouseEnter={() => tipIn(f.row.id)} onMouseLeave={tipOut} title={f.status.label} style={{ flex: "none", position: "relative", display: "inline-flex", cursor: "default" }}>
                    <StatusGlyph status={f.status} />
                    {tip === f.row.id && <StatusTip who={`${f.status.label} · ${f.tipWho}`} what={f.tipWhat} style={{ left: -6, top: 24 }} />}
                  </span>
                  <span style={{ flex: "1 1 200px", minWidth: 160, display: "flex", flexDirection: "column", gap: 2, overflow: "hidden" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                      <span data-testid="row-title" style={{ ...ELLIPSIS, fontSize: 13.5, fontWeight: 500 }}>{f.row.title}</span>
                      {f.photos > 0 && <PhotoCount n={f.photos} />}
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: DS.meta, whiteSpace: "nowrap", overflow: "hidden" }}>
                      {f.company && (
                        <>
                          <CompanySwatch name={f.company} />
                          <span style={{ flex: "none" }}>{f.company}</span>
                          <span style={{ color: DS.faint }}>·</span>
                        </>
                      )}
                      {f.locShort && (
                        <>
                          <DsIcon name="pin" size={10} color={DS.dashed} />
                          <span style={ELLIPSIS}>{f.locShort}</span>
                        </>
                      )}
                      {showSite && (
                        <>
                          {(f.company || f.locShort) && <span style={{ color: DS.faint }}>·</span>}
                          <span style={ELLIPSIS}>{siteName(f.row.jobSiteId)}</span>
                        </>
                      )}
                      {f.assignee && (
                        <>
                          <span style={{ color: DS.faint }}>·</span>
                          <span style={ELLIPSIS}>{f.assignee}</span>
                        </>
                      )}
                    </span>
                  </span>
                  {f.assignee ? <span title={f.assignee}><PersonAvatar name={f.assignee} size={22} /></span> : <NoAvatar />}
                  <span style={{ flex: "none", width: 48, textAlign: "right", fontSize: 12, color: f.overdue ? RT.overdue : RT.cell }}>
                    {f.due && !isClosed(f.status) ? shortDay(f.due) : "—"}
                  </span>
                  {hover === f.row.id && f.photos > 0 && <PhotoPeek row={f.row} count={f.photos} style={{ right: 16, top: 52 }} />}
                </div>
              ))}
            </section>
          ))}
          <PagedListEnd shown={paged.shown} total={shownFacts.length} noun={kind.singular} nouns={kind.plural} sentinel={paged.sentinel} />
        </div>
      ) : (
        /* Linear's board: the board is exactly the pane's height and every
           column fills it, so a card only ever travels sideways and every
           drop target is as tall as the tallest column. Each column scrolls
           its own cards under a header that stays put. */
        <div data-testid="records-board" style={{ flex: 1, minHeight: 0, overflowX: "auto", overflowY: "hidden", padding: "12px 16px 0", background: RT.hover, display: "flex" }}>
          <div style={{ display: "flex", gap: 14, alignItems: "stretch", minWidth: "max-content", minHeight: 0 }}>
            {boardOrder(statusGroups.filter((group) => group.facts.length > 0 || BOARD_COLUMNS[vocab].includes(group.status.key) || (!!dragging && group.status.key !== "draft"))).map((group) => {
              const source = !!dragging && draggedFrom === group.status.key;
              const over = !!dragging && !source && dropOn === group.status.key;
              const color = group.status.color;
              return (
              <section
                key={group.status.key}
                data-testid="records-board-column"
                data-group={group.status.label}
                data-drop={over ? "true" : undefined}
                onDragOver={(event) => {
                  if (!dragging) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                  if (dropOn !== group.status.key) setDropOn(group.status.key);
                }}
                onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropOn((current) => (current === group.status.key ? null : current)); }}
                onDrop={(event) => { event.preventDefault(); dropOnColumn(group.status); }}
                style={{ flex: "none", width: 328, minHeight: 0, display: "flex", flexDirection: "column", borderRadius: 14, background: over ? withAlpha(color, 0.08) : "transparent", outline: dragging && !source ? (over ? `1.5px solid ${withAlpha(color, 0.5)}` : "1px dashed rgba(10,10,10,0.12)") : "none", outlineOffset: -1, transition: "background .12s" }}
              >
                <div data-testid="records-board-column-head" style={{ flex: "none", display: "flex", alignItems: "center", gap: 9, height: 38, padding: "0 8px" }}>
                  <StatusGlyph status={group.status} />
                  <span style={{ fontSize: 13.5, fontWeight: 700 }}>{group.status.label}</span>
                  <span style={{ fontFamily: FONT_MONO, fontSize: 10.5, color: RT.count }}>{group.facts.length}</span>
                  <span style={{ flex: 1 }} />
                  {canCreate && (
                    <button
                      type="button"
                      className="rt-hover"
                      aria-label={`New ${kind.singular}`}
                      onClick={(event) => { event.stopPropagation(); setNewOpen(true); }}
                      style={{ display: "inline-flex", width: 24, height: 24, alignItems: "center", justifyContent: "center", border: "none", borderRadius: 6, background: "transparent", cursor: "pointer" }}
                    >
                      <DsIcon name="plus" size={13} color={RT.count} />
                    </button>
                  )}
                </div>
                <div
                  className="rt-board-col"
                  data-testid="records-board-column-body"
                  style={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", display: "flex", flexDirection: "column", gap: 10, padding: `2px 4px var(--ask-composer-clearance, ${ASK_COMPOSER_CLEARANCE}px)` }}
                >
                  {dragging && !source && (over || group.facts.length === 0) ? (
                    <div data-testid="records-board-drop-hint" style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 64, borderRadius: 12, border: `1.5px dashed ${color}`, fontSize: 12.5, fontWeight: 600, color, opacity: 0.8 }}>
                      {`Move to ${group.status.label}`}
                    </div>
                  ) : group.facts.length === 0 && (
                    <div data-testid="records-board-empty" style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 64, borderRadius: 12, border: `1px dashed ${withAlpha(DS.ink, 0.12)}`, fontSize: 12.5, color: RT.count }}>
                      {group.status.key === "closed" ? "Nothing closed" : `Nothing in ${group.status.label}`}
                    </div>
                  )}
                  {group.facts.map((f) => (
                    <BoardCard
                      key={f.row.id}
                      f={f}
                      issues={vocab === "issues"}
                      showSite={showSite}
                      siteName={siteName}
                      link={{ ...rowLink(f), ...dragProps(f) }}
                      lifted={dragging === f.row.id}
                      photos={f.photos > 0 ? <BoardPhotos row={f.row} count={f.photos} /> : null}
                    />
                  ))}
                </div>
              </section>
              );
            })}
          </div>
        </div>
      )}

      {newOpen && canCreate && (
        <NewRecordModal
          vocab={vocab === "punch" ? "punch" : "obs"}
          sites={sites.filter((site) => site.syncSource !== "autodesk")}
          activeSiteId={activeSiteId}
          search={search}
          onClose={() => setNewOpen(false)}
          onCreated={onCreated}
        />
      )}

      {pendingMove && (
        <MoveRecordModal
          f={pendingMove.f}
          vocab={vocab}
          from={pendingMove.f.status}
          to={pendingMove.to}
          rule={pendingMove.rule}
          search={search}
          onCancel={() => setPendingMove(null)}
          onMove={async (fill) => {
            const failure = await commitMove(pendingMove.f, pendingMove.to, fill);
            if (!failure) setPendingMove(null);
            return failure;
          }}
        />
      )}

      {toast && (
        <div
          role="status"
          data-testid="records-toast"
          className="rt-fade"
          style={{ position: "absolute", left: "50%", bottom: "calc(var(--ask-composer-clearance, 208px) - 40px)", transform: "translateX(-50%)", zIndex: 90, display: "inline-flex", alignItems: "center", gap: 9, maxWidth: "calc(100% - 32px)", minHeight: 40, padding: "0 16px 0 12px", borderRadius: 20, background: DS.ink, color: RT.pane, fontSize: 13, fontWeight: 600, boxShadow: RT.shadowToast }}
        >
          <DsIcon name={toast.tone === "ok" ? "check" : "close"} size={13} color={toast.tone === "ok" ? RT.greenBright : DS.red} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{toast.text}</span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/** The board reads left to right as the work goes, and a rejection is not
 *  a step along it: every rejected state (an `x` glyph — Not accepted, Work
 *  Not Accepted, In Dispute) stands at the far right, after Closed and after
 *  any status nobody predicted. */
function boardOrder<T extends { status: StatusDef }>(groups: T[]): T[] {
  return [...groups.filter((group) => group.status.glyph !== "x"), ...groups.filter((group) => group.status.glyph === "x")];
}

const GROUP_LABEL: Record<RecordsGroupBy, string> = { status: "Status", location: "Location", company: "Company", assignee: "Assignee" };
const GROUP_ICON: Record<RecordsGroupBy, DsIconName> = { status: "check", location: "pin", company: "device", assignee: "person" };

const CELL: CSSProperties = {
  display: "flex",
  alignItems: "center",
  minWidth: 0,
  height: RECORDS_ROW,
  padding: "0 14px",
  borderRight: "1px solid rgba(10,10,10,0.06)",
  fontSize: 13.5,
  whiteSpace: "nowrap",
  overflow: "hidden",
  boxSizing: "border-box",
};

const TH: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 7,
  height: RECORDS_HEADER_ROW,
  padding: "0 14px",
  border: "none",
  borderRight: "1px solid rgba(10,10,10,0.06)",
  background: "transparent",
  font: "inherit",
  fontSize: 12.5,
  fontWeight: 500,
  color: DS.body2,
  whiteSpace: "nowrap",
  cursor: "pointer",
  userSelect: "none",
  textAlign: "left",
  minWidth: 0,
};

const ELLIPSIS: CSSProperties = { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

function humanizeWorkflow(value: string): string {
  const spaced = value.replace(/_/g, " ");
  return spaced.replace(/\b\w/g, (c) => c.toUpperCase());
}

function syncLabel(sync: NonNullable<RecordFacts["sync"]>): string {
  return sync === "synced" ? "Synced" : sync === "pending" ? "Pending" : "Failed";
}

function footerFor(key: string, facts: RecordFacts[]): string {
  switch (key) {
    case "title": return `${facts.length} count`;
    case "status": return `${facts.filter((f) => !isClosed(f.status)).length} open`;
    case "priority": return `${facts.filter((f) => f.priority === "high").length} high`;
    case "assignee": return `${facts.filter((f) => !f.assignee).length} unassigned`;
    case "due": return `${facts.filter((f) => f.overdue).length} overdue`;
    case "sync": return `${facts.filter((f) => f.sync === "synced").length} synced · ${facts.filter((f) => f.sync === "failed").length} failed`;
    default: return "";
  }
}

function listGroups(facts: RecordFacts[], groupBy: Exclude<RecordsGroupBy, "status">) {
  const none = groupBy === "assignee" ? "Unassigned" : groupBy === "company" ? "No company" : "No location";
  const keyOf = (f: RecordFacts) => (groupBy === "location" ? f.location : groupBy === "company" ? f.company : f.assignee) ?? none;
  const keys = [...new Set(facts.map(keyOf))].sort((a, b) => (a === none ? 1 : b === none ? -1 : a.localeCompare(b)));
  return keys.map((key) => {
    const members = facts.filter((f) => keyOf(f) === key);
    const isNone = key === none;
    const color = isNone ? DS.meta : groupBy === "company" ? companyTone(key).fill : groupBy === "assignee" ? avatarTone(key).bg : DS.meta;
    const mark: ReactNode =
      groupBy === "location" ? <DsIcon name="pin" size={14} color={DS.meta} />
        : groupBy === "company" ? <span style={{ flex: "none", width: 11, height: 11, borderRadius: 3, background: isNone ? RT.photoBg : companyTone(key).fill, display: "block" }} />
          : isNone ? <NoAvatar size={18} /> : <PersonAvatar name={key} size={18} />;
    return { key, label: key, facts: members, color, mark };
  });
}

function filterOptionIcon(key: string, label: string | null): ReactNode {
  if (key === "assignee") return label ? <PersonAvatar name={label} size={18} /> : <NoAvatar size={18} />;
  if (key === "company") return label ? <CompanySwatch name={label} size={9} /> : <span style={{ width: 9 }} />;
  if (key === "priority") {
    const p = label?.toLowerCase();
    return <PriorityBars priority={p === "high" || p === "medium" || p === "low" ? p : undefined} tall={11} />;
  }
  if (key === "location") return <DsIcon name="pin" size={13} color={DS.meta} />;
  return <DsIcon name="list" size={13} color={DS.meta} />;
}

function FilterChip({ icon, field, value, onRemove }: { icon: DsIconName; field: string; value: string; onRemove: () => void }) {
  return (
    <span data-testid="records-filter-chip" data-field={field} style={{ display: "inline-flex", alignItems: "center", height: 30, borderRadius: 8, border: "1px solid rgba(10,10,10,0.12)", background: RT.pane, fontSize: 13, overflow: "hidden", boxSizing: "border-box" }}>
      <span style={{ padding: "0 9px", color: DS.body2, display: "inline-flex", alignItems: "center", gap: 6 }}>
        <DsIcon name={icon} size={12} color={DS.meta} />
        {field}
      </span>
      <span style={{ padding: "0 8px", color: RT.count, borderLeft: "1px solid rgba(10,10,10,0.08)", borderRight: "1px solid rgba(10,10,10,0.08)", height: "100%", display: "inline-flex", alignItems: "center" }}>is</span>
      <span style={{ padding: "0 9px", fontWeight: 600, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</span>
      <button
        type="button"
        className="rt-hover-ink"
        aria-label={`Remove ${field} filter`}
        onClick={(event) => { event.stopPropagation(); onRemove(); }}
        style={{ width: 26, height: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", border: "none", borderLeft: "1px solid rgba(10,10,10,0.08)", background: "transparent", cursor: "pointer", color: DS.meta, padding: 0 }}
      >
        <DsIcon name="close" size={11} color="currentColor" />
      </button>
    </span>
  );
}

function CompanyBadge({ name }: { name: string }) {
  const tone = companyTone(name);
  return (
    <>
      <span aria-hidden style={{ flex: "none", width: 18, height: 18, borderRadius: 4, background: tone.fill, color: tone.ink, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 7.5, fontWeight: 800, letterSpacing: 0.2 }}>
        {companyInitials(name)}
      </span>
      <span className="rt-link" title={name} style={{ ...ELLIPSIS, color: tone.text === "inherit" ? DS.ink : tone.text }}>{name}</span>
    </>
  );
}

function PhotoCount({ n }: { n: number }) {
  return (
    <span title={`${n} ${n === 1 ? "photo" : "photos"}`} style={{ flex: "none", display: "inline-flex", alignItems: "center", gap: 3, fontFamily: FONT_MONO, fontSize: 9.5, color: RT.count }}>
      <DsIcon name="camera" size={11} color={RT.count} />
      {n}
    </span>
  );
}

function StatusTip({ who, what, style }: { who: string; what: string; style: CSSProperties }) {
  return (
    <div role="tooltip" data-testid="records-status-tip" className="rt-fade" style={{ position: "absolute", zIndex: 45, padding: "8px 10px", borderRadius: 9, background: DS.ink, color: RT.pane, fontSize: 12, lineHeight: 1.4, whiteSpace: "nowrap", boxShadow: RT.shadowTip, pointerEvents: "none", ...style }}>
      <span style={{ display: "block", fontWeight: 700 }}>{who}</span>
      <span style={{ display: "block", color: "rgba(255,255,255,0.7)" }}>{what}</span>
    </div>
  );
}

/** Up to three of the record's photos, on hover. */
/** The row's signed previews — or, from a server that predates them, its one
 *  thumbnail. */
function previewsOf(row: ConsoleRow): Array<{ url: string; capturedAt?: string }> {
  if (row.previewPhotos?.length) return row.previewPhotos;
  return row.thumbnailUrl ? [{ url: row.thumbnailUrl }] : [];
}

const preloaded = new Set<string>();
function preloadPhotos(row: ConsoleRow) {
  for (const photo of previewsOf(row)) {
    if (preloaded.has(photo.url)) continue;
    preloaded.add(photo.url);
    const image = new Image();
    image.decoding = "async";
    image.src = photo.url;
  }
}

/** A photo tile: the photo over a hatch, so a slow or missing photo reads as
 *  "photo here" rather than as a broken image. */
function PhotoTile({ url, style, loading, children }: { url?: string; style: CSSProperties; loading?: "lazy" | "eager"; children?: ReactNode }) {
  const [failed, setFailed] = useState(false);
  return (
    <span style={{ position: "relative", overflow: "hidden", background: ADR.tile, display: "block", ...style }}>
      {url && !failed && (
        <PhotoImage src={url} alt="" loading={loading} decoding="async" onError={() => setFailed(true)} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      )}
      {children}
    </span>
  );
}

function PhotoPeek({ row, count, style }: { row: ConsoleRow; count: number; style: CSSProperties }) {
  const photos = previewsOf(row);
  // As many tiles as the record has photos (up to three), even where a
  // photo could not be signed: the count is the truth, the hatch says so.
  const tiles = Array.from({ length: Math.min(3, count) }, (_, index) => photos[index]);
  if (tiles.length === 0) return null;
  return (
    <div data-testid="records-photo-peek" className="rt-fade" style={{ position: "absolute", zIndex: 40, display: "flex", gap: 6, padding: 8, background: RT.pane, border: "1px solid rgba(10,10,10,0.1)", borderRadius: 12, boxShadow: RT.shadowPopover, pointerEvents: "none", ...style }}>
      {tiles.map((photo, index) => (
        <PhotoTile key={index} url={photo?.url} loading="eager" style={{ width: 96, height: 128, borderRadius: 8 }}>
          {photo?.capturedAt && (
            <span style={{ position: "absolute", left: 6, bottom: 6, fontFamily: FONT_MONO, fontSize: 8, fontWeight: 700, color: RT.pane, background: "rgba(10,10,10,0.6)", padding: "2px 5px", borderRadius: 4 }}>
              {clock(new Date(photo.capturedAt))}
            </span>
          )}
          {index === 2 && count > 3 && <MoreBadge more={count - 3} />}
        </PhotoTile>
      ))}
    </div>
  );
}

/** The board card's photo strip: the record's first three photos, square,
 *  with how many more it holds on the last. */
function BoardPhotos({ row, count }: { row: ConsoleRow; count: number }) {
  const photos = previewsOf(row);
  const tiles = Array.from({ length: Math.min(3, count) }, (_, index) => photos[index]);
  return (
    <div data-testid="board-photos" style={{ display: "flex", gap: 6, marginTop: 10 }}>
      {tiles.map((photo, index) => (
        <PhotoTile key={index} url={photo?.url} loading="lazy" style={{ flex: "none", width: "calc((100% - 12px) / 3)", aspectRatio: "1", borderRadius: 10 }}>
          {index === 2 && count > 3 && <MoreBadge more={count - 3} />}
        </PhotoTile>
      ))}
    </div>
  );
}

function MoreBadge({ more }: { more: number }) {
  return (
    <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(10,10,10,0.45)", color: RT.pane, fontSize: 13, fontWeight: 700 }}>
      +{more}
    </span>
  );
}

function SyncCell({ f, busy, onRetry }: { f: RecordFacts; busy: boolean; onRetry: () => void }) {
  if (!f.sync) return <span style={{ color: DS.dashed }}>—</span>;
  const mark = <img src={PROCORE_MARK_URL} alt="" style={{ width: 13, height: 13, borderRadius: 3, display: "block", opacity: f.sync === "synced" ? 1 : 0.6 }} />;
  if (f.sync === "synced") {
    return (
      <>
        <span style={{ flex: "none", width: 14, height: 14, borderRadius: 7, background: DS.green, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <DsIcon name="check" size={9} color={RT.pane} />
        </span>
        {mark}
        <span data-testid="records-sync" style={{ fontSize: 12.5, fontWeight: 500 }}>Synced</span>
        {f.syncedAt && <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, color: RT.count, marginLeft: 2 }}>{shortDay(f.syncedAt)}</span>}
      </>
    );
  }
  if (f.sync === "pending") {
    return (
      <>
        <span style={{ flex: "none", width: 14, height: 14, borderRadius: 7, border: `1.5px solid ${RT.status.working}`, display: "flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box" }}>
          <span style={{ width: 6, height: 6, borderRadius: 3, background: RT.status.working, display: "block" }} />
        </span>
        {mark}
        <span data-testid="records-sync" style={{ fontSize: 12.5, fontWeight: 500 }}>Pending</span>
      </>
    );
  }
  return (
    <>
      <span style={{ flex: "none", width: 14, height: 14, borderRadius: 7, background: DS.red, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <DsIcon name="close" size={8} color={RT.pane} />
      </span>
      {mark}
      <span data-testid="records-sync" title={f.row.procoreSync?.lastError} style={{ fontSize: 12.5, fontWeight: 500 }}>Failed</span>
      <button
        type="button"
        data-testid="records-retry"
        disabled={busy}
        onClick={(event) => { event.stopPropagation(); onRetry(); }}
        style={{ marginLeft: 4, border: "none", background: "transparent", padding: 0, font: "inherit", fontSize: 12, fontWeight: 600, color: DS.ink, textDecoration: "underline", textUnderlineOffset: 2, cursor: busy ? "progress" : "pointer", opacity: busy ? 0.5 : 1 }}
      >
        {busy ? "Retrying…" : "Retry"}
      </button>
    </>
  );
}
